import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, Grid, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, SelectChangeEvent, TextField, InputAdornment, Divider } from '@mui/material';
import { Expense } from '../types/Expense';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Euro,
  ExpandMore,
  Expand,
  Compress,
  ViewList,
  TableChart,
  Label,
  Person,
  CalendarToday,
  AlignHorizontalLeft,
  SwapHoriz,
  Search,
  ArrowDropUp,
  ArrowDropDown,
  Sort
} from '@mui/icons-material';
import CustomChip from './CustomChip';
import { designTokens } from '../theme/designTokens';
import { getCategoryIcon } from '../utils/categoryIcons';
import { identifyBrand, getLogoUrl } from '../utils/brandUtils';

interface MonthlyExpensesProps {
  expenses: Expense[];
  payers: string[];
  categories: string[];
  payerColors: { [key: string]: string };
  categoryIcons?: { [key: string]: string };
}

interface SortConfig {
  key: keyof Expense;
  direction: 'asc' | 'desc';
}

const getExpenseType = (expense: Expense): string => {
  if (expense.isDebt) return 'debt';
  if (expense.shared) return 'shared';
  return 'normal';
};

const getExpenseTypeDisplay = (expense: Expense): { text: string; tooltip?: string } => {
  if (expense.shared) {
    return {
      text: 'Shared',
      tooltip: `Shared with ${expense.sharedWith}`
    };
  }
  if (expense.isDebt) {
    return { text: 'Debt' };
  }
  return { text: '-' };
};

const MonthlyExpenses: React.FC<MonthlyExpensesProps> = ({ expenses, payers, categories, payerColors, categoryIcons = {} }) => {
  const intl = useIntl();

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedPayer, setSelectedPayer] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [viewType, setViewType] = useState<'vertical' | 'horizontal'>('vertical');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'asc' });
  const [showAllMonths, setShowAllMonths] = useState(false);

  const years = useMemo(() => {
    const uniqueYears = Array.from(new Set(expenses.map(e => new Date(e.date).getFullYear())));
    return uniqueYears.sort((a, b) => b - a);
  }, [expenses]);

  const months = Array.from({ length: 12 }, (_, i) => i);

  const sortedPayers = useMemo(() => [...payers].sort((a, b) => a.localeCompare(b)), [payers]);
  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.localeCompare(b)), [categories]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      const yearMatch = expenseDate.getFullYear() === selectedYear;
      const monthMatch = showAllMonths || expenseDate.getMonth() === selectedMonth;
      const payerMatch = selectedPayer
        ? expense.payer === selectedPayer ||
        (expense.shared && expense.sharedWith === selectedPayer) ||
        (expense.isDebt && expense.debtFrom === selectedPayer)
        : true;
      const categoryMatch = selectedCategory ? expense.category === selectedCategory : true;
      const searchMatch = searchTerm
        ? expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.payer.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      return yearMatch && monthMatch && payerMatch && categoryMatch && searchMatch;
    });
  }, [expenses, selectedYear, selectedMonth, selectedPayer, selectedCategory, searchTerm, showAllMonths]);

  const expensesByPayer = useMemo(() => {
    const payerTotals: { [key: string]: { total: number, expenses: Expense[] } } = {};
    filteredExpenses.forEach(expense => {
      const relevantPayers = selectedPayer ? [selectedPayer] : payers;
      relevantPayers.forEach(payer => {
        if (!payerTotals[payer]) {
          payerTotals[payer] = { total: 0, expenses: [] };
        }
        if (expense.payer === payer) {
          const amount = expense.shared ? expense.amount / 2 : expense.amount;
          payerTotals[payer].total += amount;
          payerTotals[payer].expenses.push({ ...expense, amount });
        }
        if (expense.shared && expense.sharedWith === payer) {
          const amount = expense.amount / 2;
          payerTotals[payer].total += amount;
          payerTotals[payer].expenses.push({ ...expense, amount, payer });
        }
        if (expense.isDebt && expense.debtFrom === payer) {
          payerTotals[payer].total -= expense.amount;
          payerTotals[payer].expenses.push({ ...expense, amount: -expense.amount, payer });
        }
      });
    });
    return payerTotals;
  }, [filteredExpenses, selectedPayer, payers]);

  const totalExpenses = useMemo(() => {
    if (selectedPayer) {
      return expensesByPayer[selectedPayer]?.total || 0;
    }
    return Object.values(expensesByPayer).reduce((sum, { total }) => sum + total, 0);
  }, [expensesByPayer, selectedPayer]);

  const sortedExpenses = useMemo(() => {
    if (sortConfig.key === null) return filteredExpenses;
    return [...filteredExpenses].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredExpenses, sortConfig]);

  const requestSort = (key: keyof Expense) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (columnName: keyof Expense) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? <ArrowDropUp /> : <ArrowDropDown />;
    }
    return <Sort sx={{ fontSize: 18, ml: 0.5, opacity: 0.5 }} />;
  };

  const handleExpandAll = () => {
    setExpandedItems(Object.keys(expensesByPayer));
  };

  const handleCollapseAll = () => {
    setExpandedItems([]);
  };

  const toggleExpanded = (item: string) => {
    setExpandedItems(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handlePayerChange = (event: SelectChangeEvent<string>) => {
    const newPayer = event.target.value;
    setSelectedPayer(newPayer);
    if (newPayer === '') {
      setExpandedItems([]);
    } else {
      setExpandedItems([newPayer]);
    }
  };

  const renderAmount = (expense: Expense, payer: string) => {
    let amount = expense.amount;
    if (expense.shared) {
      amount = expense.amount / 2;
    } else if (expense.isDebt && expense.debtFrom === payer) {
      amount = -expense.amount;
    }
    return (
      <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
        <Euro sx={{ marginRight: '4px', fontSize: '1rem' }} />
        {amount.toFixed(2)}
      </Box>
    );
  };

  const renderPayerChip = (payer: string) => (
    <CustomChip
      label={payer}
      color={payerColors[payer]}
      size="small"
    />
  );

  const renderExpenseType = (expense: Expense) => {
    const { text, tooltip } = getExpenseTypeDisplay(expense);
    return tooltip ? (
      <Tooltip title={tooltip}>
        <span>{text}</span>
      </Tooltip>
    ) : (
      text
    );
  };

  const renderVerticalView = () => (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.entries(expensesByPayer).map(([payer, { total, expenses }]) => (
          <Accordion
            key={payer}
            expanded={expandedItems.includes(payer)}
            onChange={() => toggleExpanded(payer)}
            sx={{
              display: selectedPayer && selectedPayer !== payer ? 'none' : 'block',
              borderRadius: `${designTokens.borderRadius.md} !important`,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.sm,
              '&:before': { display: 'none' },
              mb: 1
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Person sx={{ color: designTokens.colors.text.secondary }} />
                  {renderPayerChip(payer)}
                </Box>
                <Typography sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.primary.main, display: 'flex', alignItems: 'center' }}>
                  <Euro sx={{ fontSize: '1.1rem', mr: 0.5 }} />
                  {total.toFixed(2)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: `${designTokens.colors.background.elevated}33` }}>
                    <TableRow>
                      <TableCell onClick={() => requestSort('category')} sx={{ cursor: 'pointer' }}>
                        <Box display="flex" alignItems="center">
                          <Label sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                          <Typography fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>
                            <FormattedMessage id="category" />
                          </Typography>
                          {getSortIcon('category')}
                        </Box>
                      </TableCell>
                      <TableCell onClick={() => requestSort('description')} sx={{ cursor: 'pointer' }}>
                        <Box display="flex" alignItems="center">
                          <AlignHorizontalLeft sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                          <Typography fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>
                            <FormattedMessage id="description" />
                          </Typography>
                          {getSortIcon('description')}
                        </Box>
                      </TableCell>
                      <TableCell align="center" onClick={() => requestSort('date')} sx={{ cursor: 'pointer' }}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <CalendarToday sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                          <Typography fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>
                            <FormattedMessage id="date" />
                          </Typography>
                          {getSortIcon('date')}
                        </Box>
                      </TableCell>
                      <TableCell align="right" onClick={() => requestSort('amount')} sx={{ cursor: 'pointer' }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Euro sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                          <Typography fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>
                            <FormattedMessage id="amount" />
                          </Typography>
                          {getSortIcon('amount')}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <SwapHoriz sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                          <Typography fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '0.9rem' }}>
                            <FormattedMessage id="type" />
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedExpenses.filter(e => e.payer === payer || (e.shared && e.sharedWith === payer) || (e.isDebt && e.debtFrom === payer)).map((expense, index) => (
                      <TableRow
                        key={`${payer}-${expense.id}-${index}`}
                        sx={{ '&:hover': { bgcolor: `${designTokens.colors.background.elevated}22` } }}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getCategoryIcon(expense.category, categoryIcons)}
                            <Typography>{expense.category}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {(() => {
                              const brand = identifyBrand(expense.description);
                              if (brand) {
                                return (
                                  <Box
                                    component="img"
                                    src={getLogoUrl(brand.domain)}
                                    alt={brand.name}
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: '4px',
                                      flexShrink: 0
                                    }}
                                    onError={(e: any) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                );
                              }
                              return null;
                            })()}
                            {expense.description}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                          {new Date(expense.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                          {renderAmount(expense, payer)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                          {renderExpenseType(expense)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Grid>
  );

  const renderHorizontalView = () => (
    <Grid item xs={12}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md,
          overflow: 'hidden'
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 300, sm: 650 } }}>
          <TableHead sx={{ bgcolor: `${designTokens.colors.background.elevated}33` }}>
            <TableRow>
              <TableCell onClick={() => requestSort('payer')} sx={{ cursor: 'pointer' }}>
                <Box display="flex" alignItems="center">
                  <Person sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="payer" />
                  {getSortIcon('payer')}
                </Box>
              </TableCell>
              <TableCell onClick={() => requestSort('category')} sx={{ cursor: 'pointer' }}>
                <Box display="flex" alignItems="center">
                  <Label sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="category" />
                  {getSortIcon('category')}
                </Box>
              </TableCell>
              <TableCell onClick={() => requestSort('description')} sx={{ cursor: 'pointer' }}>
                <Box display="flex" alignItems="center">
                  <AlignHorizontalLeft sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="description" />
                  {getSortIcon('description')}
                </Box>
              </TableCell>
              <TableCell align="center" onClick={() => requestSort('date')} sx={{ cursor: 'pointer' }}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CalendarToday sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="date" />
                  {getSortIcon('date')}
                </Box>
              </TableCell>
              <TableCell align="right" onClick={() => requestSort('amount')} sx={{ cursor: 'pointer' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <Euro sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="amount" />
                  {getSortIcon('amount')}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <SwapHoriz sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />
                  <FormattedMessage id="type" />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(expensesByPayer).map(([payer, { total, expenses }]) => (
              <React.Fragment key={payer}>
                <TableRow sx={{ bgcolor: `${designTokens.colors.background.elevated}55` }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ marginRight: '8px', color: designTokens.colors.text.secondary }} />
                      {renderPayerChip(payer)}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ color: designTokens.colors.primary.main }}>
                      <Euro sx={{ marginRight: '4px', fontSize: '1rem' }} />
                      {total.toFixed(2)}
                    </Box>
                  </TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
                {sortedExpenses.filter(e => e.payer === payer || (e.shared && e.sharedWith === payer) || (e.isDebt && e.debtFrom === payer)).map((expense, index) => (
                  <TableRow
                    key={`${payer}-${expense.id}-${index}`}
                    sx={{ '&:hover': { bgcolor: `${designTokens.colors.background.elevated}22` } }}
                  >
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}></TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getCategoryIcon(expense.category, categoryIcons)}
                        <Typography>{expense.category}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {(() => {
                          const brand = identifyBrand(expense.description);
                          if (brand) {
                            return (
                              <Box
                                component="img"
                                src={getLogoUrl(brand.domain)}
                                alt={brand.name}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '4px',
                                  flexShrink: 0
                                }}
                                onError={(e: any) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                        {expense.description}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      {new Date(expense.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      {renderAmount(expense, payer)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      {renderExpenseType(expense)}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: 3,
        p: 3,
        borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.primary.gradient,
        color: '#fff',
        boxShadow: designTokens.shadows.md
      }}>
        <Typography variant="h5" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
          <FormattedMessage id="total" />
        </Typography>
        <Typography variant="h3" sx={{
          fontWeight: designTokens.typography.fontWeight.bold,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Euro sx={{ fontSize: '2.5rem', mr: 1 }} />
          {totalExpenses.toFixed(2)}
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="year-select-label"><FormattedMessage id="year" /></InputLabel>
              <Select
                labelId="year-select-label"
                value={selectedYear}
                label={intl.formatMessage({ id: "year" })}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="month-select-label"><FormattedMessage id="month" /></InputLabel>
              <Select
                labelId="month-select-label"
                value={showAllMonths ? 'all' : selectedMonth}
                label={intl.formatMessage({ id: "month" })}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'all') {
                    setShowAllMonths(true);
                    setSelectedMonth(0);
                  } else {
                    setShowAllMonths(false);
                    setSelectedMonth(Number(value));
                  }
                }}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                <MenuItem value="all"><FormattedMessage id="allMonths" /></MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {intl.formatMessage({ id: `month.${month}` })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="payer-select-label"><FormattedMessage id="payer" /></InputLabel>
              <Select
                labelId="payer-select-label"
                value={selectedPayer}
                label={intl.formatMessage({ id: "payer" })}
                onChange={handlePayerChange}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                <MenuItem value=""><FormattedMessage id="allPayers" /></MenuItem>
                {sortedPayers.map(payer => (
                  <MenuItem key={payer} value={payer}>{payer}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-select-label"><FormattedMessage id="category" /></InputLabel>
              <Select
                labelId="category-select-label"
                value={selectedCategory}
                label={intl.formatMessage({ id: "category" })}
                onChange={(e) => setSelectedCategory(e.target.value as string)}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                <MenuItem value=""><FormattedMessage id="allCategories" /></MenuItem>
                {sortedCategories.map(category => (
                  <MenuItem key={category} value={category}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getCategoryIcon(category, categoryIcons)}
                      <Typography>{category}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1, opacity: 0.5 }} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder={intl.formatMessage({ id: "search" })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: designTokens.colors.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: designTokens.borderRadius.md }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Tooltip title={intl.formatMessage({ id: "verticalView" })}>
                <IconButton
                  onClick={() => setViewType('vertical')}
                  sx={{
                    bgcolor: viewType === 'vertical' ? designTokens.colors.primary.main : 'transparent',
                    color: viewType === 'vertical' ? '#fff' : designTokens.colors.text.secondary,
                    '&:hover': { bgcolor: viewType === 'vertical' ? designTokens.colors.primary.main : designTokens.colors.background.elevated }
                  }}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: "horizontalView" })}>
                <IconButton
                  onClick={() => setViewType('horizontal')}
                  sx={{
                    bgcolor: viewType === 'horizontal' ? designTokens.colors.primary.main : 'transparent',
                    color: viewType === 'horizontal' ? '#fff' : designTokens.colors.text.secondary,
                    '&:hover': { bgcolor: viewType === 'horizontal' ? designTokens.colors.primary.main : designTokens.colors.background.elevated }
                  }}
                >
                  <TableChart />
                </IconButton>
              </Tooltip>
              <Box sx={{ width: 1, height: 24, alignSelf: 'center', mx: 1, borderLeft: `1px solid ${designTokens.colors.background.elevated}` }} />
              <Tooltip title={intl.formatMessage({ id: "expandAll" })}>
                <IconButton onClick={handleExpandAll} sx={{ color: designTokens.colors.text.secondary }}>
                  <Expand />
                </IconButton>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: "collapseAll" })}>
                <IconButton onClick={handleCollapseAll} sx={{ color: designTokens.colors.text.secondary }}>
                  <Compress />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {viewType === 'vertical' ? renderVerticalView() : renderHorizontalView()}
    </Box>
  );
};

export default MonthlyExpenses;